import { apiClient } from './client';
import { Payer } from './expenses';

export type FounderTransactionKind = 'contribution' | 'reimbursement' | 'profit';

export interface FounderTransaction {
  id: string;
  date: string; // YYYY-MM-DD
  payer_id: string;
  payer: Payer;
  kind: FounderTransactionKind;
  amount: number;
  comment: string | null;
  created_at: string;
  updated_at: string;
}

export interface BankBalanceCheck {
  id: string;
  date: string;
  amount: number;
  comment: string | null;
  created_at: string;
}

export interface FounderBalance {
  payerId: string;
  name: string;
  profitShare: number;
  contributed: number;
  reimbursed: number;
  debt: number;
  profitTaken: number;
  profitEntitled: number;
  profitAvailable: number;
}

export interface TreasuryOverview {
  cash: {
    settled: number;
    contributions: number;
    companyExpenses: number;
    reimbursements: number;
    profitPayouts: number;
    total: number;
  };
  founders: FounderBalance[];
  totalDebt: number;
  distributableProfit: number;
  lifetimeProfit: number;
  bankCheck: { date: string; amount: number; discrepancy: number } | null;
  transactions: FounderTransaction[];
  checks: BankBalanceCheck[];
}

export interface CreateFounderTransactionDto {
  date: string;
  payerId: string;
  kind: FounderTransactionKind;
  amount: number;
  comment?: string | null;
}

export type UpdateFounderTransactionDto = Partial<CreateFounderTransactionDto>;

export interface CreateBankBalanceCheckDto {
  date: string;
  amount: number;
  comment?: string | null;
}

export const treasuryApi = {
  getOverview: async (): Promise<TreasuryOverview> => {
    const response = await apiClient.get<TreasuryOverview>('/treasury/overview');
    return response.data;
  },
  createTransaction: async (dto: CreateFounderTransactionDto): Promise<FounderTransaction> => {
    const response = await apiClient.post<FounderTransaction>('/treasury/transactions', dto);
    return response.data;
  },
  updateTransaction: async (
    id: string,
    dto: UpdateFounderTransactionDto,
  ): Promise<FounderTransaction> => {
    const response = await apiClient.patch<FounderTransaction>(`/treasury/transactions/${id}`, dto);
    return response.data;
  },
  deleteTransaction: async (id: string): Promise<void> => {
    await apiClient.delete(`/treasury/transactions/${id}`);
  },
  createBalanceCheck: async (dto: CreateBankBalanceCheckDto): Promise<BankBalanceCheck> => {
    const response = await apiClient.post<BankBalanceCheck>('/treasury/balance-checks', dto);
    return response.data;
  },
  deleteBalanceCheck: async (id: string): Promise<void> => {
    await apiClient.delete(`/treasury/balance-checks/${id}`);
  },
};
